import { useState } from 'react';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import {
    App,
    Button,
    Collapse,
    Image,
    Input,
    Space,
    Tag,
    Typography,
    Upload,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { uploadAttachmentByPresign } from '@/services/attachments';
import { resolveMiniDramaCdnUrl } from '@/utils/mini-drama-assets';
import {
    getDefaultBannerDescription,
    getDefaultBannerTitle,
    hasCustomBannerImage,
    hasCustomThumbImage,
    isBannerDescriptionOverridden,
    isBannerTitleOverridden,
    moveBannerItem,
    resolveBannerDisplayUrl,
    resolveBannerPreviewFields,
    resolveBannerTitle,
    resolveThumbDisplayUrl,
    updateBannerItem,
} from '@/pages/client-banner/utils';

import type { BannerEditorItem } from '@/types/theater-banner';

type Props = {
    items: BannerEditorItem[];
    disabled?: boolean;
    onChange: (items: BannerEditorItem[]) => void;
};

const BannerList: React.FC<Props> = ({ items, disabled, onChange }) => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);

    const handleUpload = async (
        file: File,
        dramaId: string,
        field: 'banner' | 'thumb' | 'previewVideo',
    ) => {
        const uploadKey = `${dramaId}:${field}`;
        setUploadingKey(uploadKey);
        try {
            // 用 CDN URL 存入编辑态，上传后可立即预览（CDN 按需回源 S3）
            const { path } = await uploadAttachmentByPresign(file);
            const displayUrl = resolveMiniDramaCdnUrl(path);
            const uploadedField = (
                {
                    banner: 'uploadedBannerUrl',
                    thumb: 'uploadedThumbUrl',
                    previewVideo: 'uploadedPreviewVideoUrl',
                } as const
            )[field];

            onChange(updateBannerItem(items, dramaId, { [uploadedField]: displayUrl }));
            message.success(formatMessage({ id: 'clientBanner.upload.success' }));
        } catch (error: any) {
            message.error(error?.message || formatMessage({ id: 'clientBanner.upload.failed' }));
        } finally {
            setUploadingKey(null);
        }
    };

    if (!items.length) {
        return (
            <Typography.Text type="secondary">
                <FormattedMessage id="clientBanner.list.empty" />
            </Typography.Text>
        );
    }

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {items.map((item, index) => {
                const bannerUrl = resolveBannerDisplayUrl(item);
                const thumbUrl = resolveThumbDisplayUrl(item);
                const defaultTitle = getDefaultBannerTitle(item);
                const defaultDescription = getDefaultBannerDescription(item);
                const preview = resolveBannerPreviewFields(item);

                return (
                    <Collapse
                        key={item.dramaId}
                        items={[
                            {
                                key: item.dramaId,
                                label: (
                                    <Space wrap>
                                        <Tag>{index + 1}</Tag>
                                        <Typography.Text strong>{resolveBannerTitle(item)}</Typography.Text>
                                        {item.missing && (
                                            <Tag color="error">
                                                <FormattedMessage id="clientBanner.list.missing" />
                                            </Tag>
                                        )}
                                    </Space>
                                ),
                                extra: (
                                    <Space onClick={(event) => event.stopPropagation()}>
                                        <Button
                                            size="small"
                                            icon={<ArrowUpOutlined />}
                                            disabled={disabled || index === 0}
                                            onClick={() => onChange(moveBannerItem(items, index, 'up'))}
                                        />
                                        <Button
                                            size="small"
                                            icon={<ArrowDownOutlined />}
                                            disabled={disabled || index === items.length - 1}
                                            onClick={() => onChange(moveBannerItem(items, index, 'down'))}
                                        />
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            disabled={disabled}
                                            onClick={() =>
                                                onChange(
                                                    items
                                                        .filter((row) => row.dramaId !== item.dramaId)
                                                        .map((row, sortIndex) => ({ ...row, sortOrder: sortIndex })),
                                                )
                                            }
                                        />
                                    </Space>
                                ),
                                children: (
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        <Space align="start" wrap>
                                            <Space direction="vertical" size={4} align="center">
                                                <Space align="center" size={4}>
                                                    <Typography.Text type="secondary">
                                                        <FormattedMessage id="clientBanner.list.bannerPreview" />
                                                    </Typography.Text>
                                                    {hasCustomBannerImage(item) ? (
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            disabled={disabled}
                                                            onClick={() =>
                                                                onChange(
                                                                    updateBannerItem(items, item.dramaId, {
                                                                        uploadedBannerUrl: undefined,
                                                                        customBannerUrl: undefined,
                                                                    }),
                                                                )
                                                            }
                                                        >
                                                            <FormattedMessage id="clientBanner.list.restoreDefault" />
                                                        </Button>
                                                    ) : null}
                                                </Space>
                                                {bannerUrl ? (
                                                    <Image src={bannerUrl} width={160} height={80} style={{ objectFit: 'cover' }} />
                                                ) : (
                                                    '-'
                                                )}
                                                <Upload
                                                    showUploadList={false}
                                                    accept="image/*"
                                                    disabled={disabled}
                                                    customRequest={({ file, onSuccess, onError }) => {
                                                        handleUpload(file as File, item.dramaId, 'banner')
                                                            .then(() => onSuccess?.('ok'))
                                                            .catch(onError);
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        icon={<UploadOutlined />}
                                                        loading={uploadingKey === `${item.dramaId}:banner`}
                                                    >
                                                        <FormattedMessage id="clientBanner.list.uploadBanner" />
                                                    </Button>
                                                </Upload>
                                            </Space>
                                            <Space direction="vertical" size={4} align="center">
                                                <Space align="center" size={4}>
                                                    <Typography.Text type="secondary">
                                                        <FormattedMessage id="clientBanner.list.thumbPreview" />
                                                    </Typography.Text>
                                                    {hasCustomThumbImage(item) ? (
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            disabled={disabled}
                                                            onClick={() =>
                                                                onChange(
                                                                    updateBannerItem(items, item.dramaId, {
                                                                        uploadedThumbUrl: undefined,
                                                                        customThumbUrl: undefined,
                                                                    }),
                                                                )
                                                            }
                                                        >
                                                            <FormattedMessage id="clientBanner.list.restoreDefault" />
                                                        </Button>
                                                    ) : null}
                                                </Space>
                                                {thumbUrl ? (
                                                    <Image src={thumbUrl} width={80} height={45} style={{ objectFit: 'cover' }} />
                                                ) : (
                                                    '-'
                                                )}
                                                <Upload
                                                    showUploadList={false}
                                                    accept="image/*"
                                                    disabled={disabled}
                                                    customRequest={({ file, onSuccess, onError }) => {
                                                        handleUpload(file as File, item.dramaId, 'thumb')
                                                            .then(() => onSuccess?.('ok'))
                                                            .catch(onError);
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        icon={<UploadOutlined />}
                                                        loading={uploadingKey === `${item.dramaId}:thumb`}
                                                    >
                                                        <FormattedMessage id="clientBanner.list.uploadThumb" />
                                                    </Button>
                                                </Upload>
                                            </Space>
                                        </Space>

                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                                                <Typography.Text strong>
                                                    <FormattedMessage id="clientBanner.list.titleLabel" />
                                                </Typography.Text>
                                                {isBannerTitleOverridden(item) ? (
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        disabled={disabled}
                                                        onClick={() =>
                                                            onChange(
                                                                updateBannerItem(items, item.dramaId, {
                                                                    titleOverride: undefined,
                                                                }),
                                                            )
                                                        }
                                                    >
                                                        <FormattedMessage id="clientBanner.list.restoreDefault" />
                                                    </Button>
                                                ) : null}
                                            </Space>
                                            <Input
                                                placeholder={defaultTitle}
                                                value={item.titleOverride !== undefined ? item.titleOverride : defaultTitle}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    onChange(
                                                        updateBannerItem(items, item.dramaId, {
                                                            titleOverride: event.target.value,
                                                        }),
                                                    )
                                                }
                                            />
                                            {defaultTitle ? (
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    <FormattedMessage
                                                        id="clientBanner.list.defaultTitle"
                                                        values={{ title: defaultTitle }}
                                                    />
                                                </Typography.Text>
                                            ) : null}
                                        </Space>

                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                                                <Typography.Text strong>
                                                    <FormattedMessage id="clientBanner.list.descriptionLabel" />
                                                </Typography.Text>
                                                {isBannerDescriptionOverridden(item) ? (
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        disabled={disabled}
                                                        onClick={() =>
                                                            onChange(
                                                                updateBannerItem(items, item.dramaId, {
                                                                    descriptionOverride: undefined,
                                                                }),
                                                            )
                                                        }
                                                    >
                                                        <FormattedMessage id="clientBanner.list.restoreDefault" />
                                                    </Button>
                                                ) : null}
                                            </Space>
                                            <Input.TextArea
                                                rows={2}
                                                placeholder={defaultDescription || formatMessage({ id: 'clientBanner.list.descriptionOverride' })}
                                                value={item.descriptionOverride !== undefined ? item.descriptionOverride : defaultDescription}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    onChange(
                                                        updateBannerItem(items, item.dramaId, {
                                                            descriptionOverride: event.target.value,
                                                        }),
                                                    )
                                                }
                                            />
                                            {defaultDescription ? (
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    <FormattedMessage
                                                        id="clientBanner.list.defaultDescription"
                                                        values={{ description: defaultDescription }}
                                                    />
                                                </Typography.Text>
                                            ) : null}
                                        </Space>

                                        {(() => {
                                            const hasStats =
                                                preview &&
                                                (preview.tags?.length ||
                                                    preview.totalEpisodes != null ||
                                                    preview.creatorName ||
                                                    preview.totalPlayCount != null ||
                                                    preview.totalHeatValue != null ||
                                                    preview.avgRating != null ||
                                                    preview.actorAvatarUrls?.length);

                                            if (!hasStats) return null;

                                            return (
                                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                                    <Typography.Text strong>
                                                        <FormattedMessage id="clientBanner.list.dramaMetaLabel" />
                                                    </Typography.Text>
                                                    {preview!.tags?.length ? (
                                                        <Space wrap size={[4, 4]}>
                                                            {preview!.tags.map((tag) => (
                                                                <Tag key={tag}>{tag}</Tag>
                                                            ))}
                                                        </Space>
                                                    ) : null}
                                                    {(preview!.totalEpisodes != null ||
                                                        preview!.creatorName ||
                                                        preview!.totalPlayCount != null ||
                                                        preview!.totalHeatValue != null ||
                                                        preview!.avgRating != null) && (
                                                        <Typography.Text type="secondary">
                                                            <FormattedMessage
                                                                id="clientBanner.list.dramaMeta"
                                                                values={{
                                                                    episodes: preview!.totalEpisodes ?? '-',
                                                                    creator: preview!.creatorName ?? '-',
                                                                    plays: preview!.totalPlayCount ?? '-',
                                                                    heat: preview!.totalHeatValue ?? '-',
                                                                    rating: preview!.avgRating ?? '-',
                                                                }}
                                                            />
                                                        </Typography.Text>
                                                    )}
                                                    {preview!.actorAvatarUrls?.length ? (
                                                        <Image.PreviewGroup>
                                                            <Space size={8}>
                                                                {preview!.actorAvatarUrls.map((avatar) => (
                                                                    <Image
                                                                        key={avatar}
                                                                        src={avatar}
                                                                        width={32}
                                                                        height={32}
                                                                        style={{ borderRadius: '50%', objectFit: 'cover' }}
                                                                    />
                                                                ))}
                                                            </Space>
                                                        </Image.PreviewGroup>
                                                    ) : null}
                                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                        <FormattedMessage id="clientBanner.list.dramaMetaHint" />
                                                    </Typography.Text>
                                                </Space>
                                            );
                                        })()}
                                    </Space>
                                ),
                            },
                        ]}
                    />
                );
            })}
        </Space>
    );
};

export default BannerList;
